/**
 * The two dialogs that act on a single call row.
 *
 * Moved out of CallsPage with the table, and for the same reason: the
 * dashboard's Recall list renders those rows now, and a correction started
 * there has to open the same form the Daily Calls Report opens. Two copies of
 * a form whose fields gate themselves on rank, provenance and ownership would
 * not stay the same form for long.
 *
 * Both are props in, intent out. Neither fetches, neither mutates, and
 * neither knows which screen it is on.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { formatFilterDate } from '../lib/filterDate';
import { titleCaseName, titleCaseNameOr } from '../lib/titleCase';
import { formatPhone, sanitiseMobileInput } from '../lib/phone';
import ClientSelect from './ClientSelect';
import { withStoredOutcome } from '../lib/callOutcomes';
import { recruiterSuffix } from '../lib/recruiters';
import type { Call, CallOutcome, User } from '../types';

export function EditCallDialog({
  call,
  outcomes,
  saving,
  failure,
  taking,
  onTake,
  reassignTo = [],
  reassigning = false,
  onReassign,
  onCancel,
  onSave,
}: {
  call: Call;
  outcomes: { value: CallOutcome; label: string }[];
  saving: boolean;
  failure: string | null;
  taking: boolean;
  onTake: () => void;
  /**
   * Who this call may be handed to, or empty where nobody may hand it on.
   *
   * Props in, like everything else here -- the dialog does not fetch, so the
   * page that knows whether the reader is a company admin is the page that
   * asks for the list. Empty, with `onReassign` absent, is how a screen says
   * the control is not offered. My Day says exactly that.
   */
  reassignTo?: User[];
  reassigning?: boolean;
  onReassign?: (recruiterUserId: string) => void;
  onCancel: () => void;
  onSave: (changes: Record<string, unknown>) => void;
}) {
  const [clientId, setClientId] = useState(call.clientId ?? '');
  const [position, setPosition] = useState(call.position ?? '');
  const [outcome, setOutcome] = useState<CallOutcome>(call.outcome);
  // Whatever this call already holds, offered even where the picker has
  // retired it -- otherwise correcting the client on an Interested call from
  // last month rewrites its outcome as a side effect.
  const outcomeOptions = withStoredOutcome(outcomes, call.outcome);
  const [remarks] = useState(call.remarks ?? '');
  const editableOutcome = call.isLatestForCandidate !== false;
  const [candidateName, setCandidateName] = useState(call.candidateName ?? '');
  const [candidatePhone, setCandidatePhone] = useState(call.candidatePhone ?? '');
  const [callDate, setCallDate] = useState(call.callDate);
  /**
   * Who the call would go to, which starts as whoever already has it.
   *
   * Held apart from the fields above and never included in `changed()`:
   * moving a call is its own endpoint and its own act, so it happens on its
   * own button rather than riding along with a corrected position. Save and
   * Move are two decisions, and a reader should not be able to make the
   * second by accident while making the first.
   */
  const [recruiterUserId, setRecruiterUserId] = useState(call.recruiterUserId);
  const canReassign = Boolean(onReassign) && reassignTo.length > 0;
  //: The account holding it now may not be one that can be given work -- most
  //: of these rows sit under an imported or departed name. It is still what
  //: the field has to read before anybody touches it.
  const ownerListed = reassignTo.some((u) => u.id === call.recruiterUserId);

  /** Only what moved. See the note above — this is the whole contract. */
  function changed(): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    const nextClient = clientId || null;
    const nextCandidateName = candidateName.trim();
    const nextCandidatePhone = candidatePhone.trim() || null;
    const nextPosition = position.trim() || null;
    const nextRemarks = remarks.trim() || null;

    if (nextClient !== (call.clientId ?? null)) body.clientId = nextClient;
    if (nextCandidateName !== (call.candidateName ?? '')) body.candidateName = nextCandidateName;
    if (nextCandidatePhone !== (call.candidatePhone ?? null)) body.candidatePhone = nextCandidatePhone;
    if (callDate && callDate !== call.callDate) body.callDate = callDate;
    if (nextPosition !== (call.position ?? null)) body.position = nextPosition;
    if (nextRemarks !== (call.remarks ?? null)) body.remarks = nextRemarks;
    if (outcome !== call.outcome) body.outcome = outcome;
    return body;
  }

  const pending = Object.keys(changed()).length > 0;

  // pt-16 rather than the p-4 it had all round. Top-aligned and scrollable is
  // right for this one -- it is tall enough to outgrow a short laptop window,
  // and centring it would put the header out of reach -- but sixteen pixels of
  // gap left it jammed against the top of the viewport, over the nav, reading
  // as something that had failed to position itself rather than as a dialog.
  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/30 p-4 pt-16">
      <div className="my-8 w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-gray-900">
          Edit {titleCaseName(call.candidateName) ?? 'this call'}&rsquo;s call
        </h3>
        <p className="mt-0.5 text-xs text-gray-500">
          {formatFilterDate(call.callDate)}
          {call.recruiterName ? ` · ${titleCaseName(call.recruiterName)}` : ''}
        </p>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSave(changed());
          }}
        >
          <div>
            <label className="label" htmlFor="ec-candidate-name">Candidate name</label>
            <input
              id="ec-candidate-name"
              className="input"
              maxLength={160}
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="ec-candidate-phone">Mobile number</label>
            <input
              id="ec-candidate-phone"
              className="input"
              inputMode="numeric"
              maxLength={10}
              value={candidatePhone}
              onChange={(e) => setCandidatePhone(sanitiseMobileInput(e.target.value))}
              placeholder="10-digit mobile number"
            />
          </div>

          {/* Taking over follow-up.
              Not a free choice of recruiter: a recruiter may put their own
              name here and nobody else's, which is the whole act -- "I am
              carrying this one from now on". A manager moving work between two
              other people is a different decision and is not this dialog's.

              Disabled with the server's own sentence when it cannot be taken,
              rather than hidden. A control that vanishes teaches nobody which
              rows can be picked up and which belong to a colleague. */}
          {/* Imported rows only, and temporary.

              Two spreadsheet defects put around 21,000 imported calls in the
              future -- a fill handle dragged down fifty years, and Excel
              reading DD.MM cells month-first. The bulk is repaired in SQL;
              this is for the residue, where the right date needs somebody who
              knows what happened.

              It disappears from a row the moment that row is not imported,
              which is also how the control retires: when the imported data is
              clean there is nothing left to point it at. The server refuses
              the same three things it refuses here, so this is a convenience
              and not the boundary. */}
          {call.dateIsCorrectable && (
            <div>
              <label className="label" htmlFor="ec-call-date">Date</label>
              <input
                id="ec-call-date"
                type="date"
                className="input"
                value={callDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setCallDate(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                This call came from a spreadsheet. Correct the date if the
                import read it wrongly.
              </p>
            </div>
          )}

          {/* Two versions of one field, because two different people are
              looking at it.

              A recruiter gets the name and a Take over button: they may put
              their own name here and nobody else's, which is the whole act --
              "I am carrying this one from now on" -- and only on a row whose
              owner cannot work it.

              A company admin gets the list. They are not taking anything; they
              are moving one person's work to another, which the server allows
              them and nobody else. Take over is dropped for them rather than
              shown beside it: it would be a second button doing a narrower
              version of what the picker already does, refusing in cases the
              picker allows, and two controls over one column that disagree
              about when they work is how somebody concludes the screen is
              broken. An admin who wants the call themselves picks their own
              name. */}
          <div>
            <label className="label" htmlFor="ec-recruiter">Recruiter</label>
            <div className="flex items-center gap-2">
              {canReassign ? (
                <select
                  id="ec-recruiter"
                  className="input flex-1"
                  value={recruiterUserId}
                  disabled={reassigning || saving}
                  onChange={(e) => setRecruiterUserId(e.target.value)}
                >
                  {/* The current owner first and always, even when they are
                      not somebody work can be given to. Most of these rows
                      belong to an imported or departed account, and a field
                      that quietly showed a different name than the row does
                      would be wrong about who owns it until somebody pressed
                      Move. */}
                  {!ownerListed && (
                    <option value={call.recruiterUserId}>
                      {titleCaseNameOr(call.recruiterName, 'Nobody who signs in')}
                    </option>
                  )}
                  {reassignTo.map((person) => (
                    <option key={person.id} value={person.id}>
                      {titleCaseName(person.fullName)}
                      {recruiterSuffix(person)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="ec-recruiter"
                  className="input flex-1 bg-gray-50 text-gray-600"
                  value={titleCaseNameOr(call.recruiterName, '—')}
                  readOnly
                  disabled
                />
              )}

              {canReassign ? (
                <button
                  type="button"
                  className="btn-secondary whitespace-nowrap text-xs"
                  disabled={
                    recruiterUserId === call.recruiterUserId
                    || reassigning
                    || saving
                  }
                  onClick={() => onReassign?.(recruiterUserId)}
                  title="Move this call, and its pending follow-up, to them"
                >
                  {reassigning ? 'Moving…' : 'Move'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-secondary whitespace-nowrap text-xs"
                  disabled={Boolean(call.takeBlocker) || taking || saving}
                  onClick={onTake}
                  title={call.takeBlocker ?? 'Take over follow-up on this call'}
                >
                  {taking ? 'Taking…' : 'Take over'}
                </button>
              )}
            </div>

            {/* Said before the press rather than after it. The follow-up
                moving is the part nobody expects, and it is the part that
                decides whose Recall list the chase lands on tomorrow. */}
            {canReassign && recruiterUserId !== call.recruiterUserId && (
              <p className="mt-1 text-xs text-amber-700">
                Moves this call and any pending follow-up on it. Their other
                calls and the candidate stay where they are.
              </p>
            )}
            {!canReassign && call.takeBlocker && (
              <p className="mt-1 text-xs text-gray-500">{call.takeBlocker}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="ec-client">Client Name</label>

            {/* Every client. The imported ones were excluded here while
                "CtrlAlt Heal", "CrlAltHeal" and "Ctrlaltheal" were one company
                across three rows, because offering all three is how a
                correction picks the wrong one. Merging collapsed those rows;
                what is left is the company, and a correction that cannot name
                it is not a correction.

                The client already on the call stays selectable whatever its
                state -- ClientSelect keeps the attached id in its options --
                so opening this dialog never silently blanks a field, and
                saving without touching it changes nothing. */}
            <ClientSelect
              purpose="work"
              id="ec-client"
              value={clientId}
              onChange={setClientId}
              placeholder="No client"
            />
          </div>

          <div>
            <label className="label" htmlFor="ec-position">Position Name</label>

            <input
              spellCheck={false}
              id="ec-position"
              className="input"
              maxLength={160}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Phlebotomist"
            />
          </div>

          <div>
            <label className="label" htmlFor="ec-outcome">Recruiter Remarks</label>

            <select
              id="ec-outcome"
              className="input"
              value={outcome}
              disabled={!editableOutcome}
              onChange={(e) => setOutcome(e.target.value as CallOutcome)}
            >
              {outcomeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {!editableOutcome && (
              <p className="mt-1 text-xs text-gray-500">
                There is a later call to this candidate, so this one&rsquo;s
                outcome no longer decides anything. Correct the latest call
                instead.
              </p>
            )}
            {editableOutcome && outcome !== call.outcome && (
              <p className="mt-1 text-xs text-amber-700">
                Changing this moves them on or off the Interested list, and
                reschedules or closes the follow-up this call created.
              </p>
            )}
          </div>

          {/* The server's own words. A refusal here names what is in the way —
              a submission, a placement, a later call — and that sentence is
              more use than any wording this form could invent for it. */}
          {failure && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {failure}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-ghost" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || !pending}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
/**
 * A second call to somebody already on the report.
 *
 * A new row, not an edit of the old one. Three things depend on that and none
 * of them survives overwriting an outcome:
 *
 * * The pending followup a commitment created is closed by *logging a call*,
 *   stamped with the call that closed it. Editing the old row leaves the chase
 *   pending, nagging on Recall about a conversation that demonstrably happened.
 * * Which call is the most recent decides who appears on the Interested list
 *   and what this report leaves out. Rewriting an outcome restates both, with
 *   no record that a second conversation took place.
 * * Three callbacks are three attempts. Mutating one row through all of them
 *   loses the fact that anybody rang twice.
 *
 * So everything that describes *who* was rung is carried across and shown
 * read-only, and the only inputs are the two things this call is actually
 * about: what they said, and in whose words.
 */
export function FollowUpDialog({
  call,
  outcomes,
  submitting,
  onCancel,
  onConfirm,
}: {
  call: Call;
  outcomes: { value: CallOutcome; label: string }[];
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (outcome: CallOutcome, remarks: string) => void;
}) {
  // Call Back, rather than the Interested this defaulted to -- which is no
  // longer offered, so the dialog would have opened on a value its own select
  // did not contain. Deliberately not Shortlisted: a dialog arriving pre-set
  // to the one outcome meaning "going forward" puts a commitment one Enter
  // away from somebody who only wanted to log a call.
  const [outcome, setOutcome] = useState<CallOutcome>('call_back');


  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card w-full max-w-sm space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(outcome, '');
        }}
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Follow up with {titleCaseName(call.candidateName) ?? 'this candidate'}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Logs a second call. The one on {call.callDate} is kept.
          </p>
        </div>

        {/* Carried from the call being followed up, so the same conversation
            is not described two different ways. Shown rather than hidden:
            a recruiter about to record an outcome should see which call and
            which client it lands against. */}
        <dl className="space-y-1 rounded-lg bg-gray-50 px-3 py-2 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Candidate</dt>
            <dd className="truncate text-gray-900">{titleCaseName(call.candidateName) ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Mobile</dt>
            <dd className="tabular-nums text-gray-900">
              {formatPhone(call.candidatePhone) ?? '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Client Name</dt>

            <dd className="truncate text-gray-900">{call.clientName ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Position Name</dt>

            <dd className="truncate text-gray-900">{call.position ?? '—'}</dd>
          </div>
        </dl>

        <label className="block text-xs text-gray-600">
          What Did They Say This Time?
          <select
            className="input mt-1"
            autoFocus
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as CallOutcome)}
          >
            {outcomes.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        {/* The free-text box is gone. The outcome list covers what it was
            for, and an administrator can add a value when it does not --
            which is the trade the client asked for.

            What is stored stays stored. 58,324 of 62,019 calls carry
            remarks, the importer keeps the raw text deliberately because
            normalising it into an outcome is lossy, and
            backfill_call_outcomes re-derives outcomes from it. Removing the
            input is not removing the column. */}


        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Log this call
          </button>
        </div>
      </form>
    </div>
  );
}
